<template>
  <!-- listen to "keyup", but also "change" to update when the user selects a country -->
  <input
    :id="id"
    ref="inputRef"
    type="tel"
    v-bind="filteredAttrs"
    @change="onChange"
    @keyup="onChange"
    v-on="filteredListeners"
  />
</template>

<script lang="ts">

import { defineComponent } from 'vue';
import intlTelInput from 'intl-tel-input';
import 'intl-tel-input/build/css/intlTelInput.css';
import { subsetObject } from '@/shared';

const utilsScript = 'https://cdn.jsdelivr.net/npm/intl-tel-input@23.9.1/build/js/utils.js';

interface IPhoneInputState {
  inputData: Record<string, any> | null
}

export default defineComponent({
  props: {
    value: {
      type: String,
      default: '',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    isInternational: {
      type: Boolean,
      default: true,
    },
    id: {
      type: String,
    },
  },

  data(): IPhoneInputState {
    return {
      inputData: null,
    };
  },

  computed: {
    filteredAttrs() {
      return subsetObject(this.$attrs, ['value', 'type', 'disabled']);
    },
    filteredListeners() {
      return subsetObject(this.$listeners, ['change', 'keyup', 'input']);
    },
  },

  watch: {
    value: {
      immediate: true,
      handler() {
        this.setValue();
      },
    },
  },

  methods: {
    setValue() {
      // It's a hack where mounted methods and immediate watcher handlers can read `$refs`. $refs is undefined otherwise.
      setTimeout(() => {
        if (!this.inputData) {
          this.inputData = intlTelInput(this.$refs.inputRef as HTMLInputElement, {
            utilsScript,
            allowDropdown: this.isInternational,
            containerClass: 'iti-container',
            formatAsYouType: true,
            formatOnDisplay: true,
            // i18n: {}, // If we want to add i18n in the future
            // Note that the area code is taken into consideration when determining the flag
            initialCountry: 'ca',
            countryOrder: ['ca', 'us'],
            separateDialCode: this.isInternational,
            strictMode: true,
          });
        }

        // for redundancy
        if (this.isInternational) {
          this.inputData.setCountry('ca');
        }

        if (this.disabled) {
          this.inputData.setDisabled(true);
        }

        this.inputData.setNumber(this.value);
      }, 0);
    },

    onChange() {
      if (!this.inputData) {
        return;
      }

      const phoneNumber = this.inputData.getNumber(
        this.isInternational ? intlTelInput.utils?.numberFormat.E164 : intlTelInput.utils?.numberFormat.NATIONAL,
      );
      const rawVal = this.inputData.telInput?.value?.trim() ?? '';
      const isValid = this.inputData.isValidNumber();

      if (isValid || !rawVal.length) {
        this.$emit('input', phoneNumber);
      }

      this.$emit('valid', rawVal.length > 0 ? isValid : true);
    },
  },
});
</script>

<style lang="scss">

.iti-container > input {
  outline: none;
}

</style>
